import { AxiosError } from "axios";
import { apiClient } from "@/lib/api-client";
import { parseContentDispositionFilename } from "../lib/parse-content-disposition";
import { extractExportErrorMessage } from "../lib/extract-export-error-message";

/**
 * MIME type for the OpenXML `.xlsx` workbook the solver-input export emits.
 * Sent as the `Accept` header and matched by the backend's `Content-Type`.
 */
export const XLSX_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Fallback filename used when the server's `Content-Disposition` header is
 * missing or unparseable (defense-in-depth — the controller always emits an
 * ASCII header, but a proxy could strip it). Kept generic and ASCII-safe so
 * the download still lands with a sensible name.
 */
export const SOLVER_INPUTS_FALLBACK_FILENAME = "solver-inputs.xlsx";

export interface SolverInputsDownloadResult {
  blob: Blob;
  filename: string;
}

/**
 * Fetches the solver-input workbook for a schedule as a Blob, returning the
 * filename parsed from `Content-Disposition` alongside it (falling back to
 * {@link SOLVER_INPUTS_FALLBACK_FILENAME} when the header is absent or
 * malformed).
 *
 * Unlike the run-scoped export bundles, this endpoint is **not** run-gated:
 * the backend returns 200 for any existing schedule (even one with zero
 * events). It requires `OrgAdminOrAbove`, so a Viewer gets 403 and an
 * unauthenticated caller gets 401.
 *
 * Routes through the shared `apiClient` so the Firebase ID token is attached
 * by the request interceptor — never a bare `fetch`. `responseType: 'blob'`
 * tells axios to skip JSON parsing on the success path.
 *
 * Errors are normalized to plain `Error` instances via the shared
 * {@link extractExportErrorMessage} helper (also used by `downloadExportBundle`
 * in `runs-api.ts`): the error body comes back as a Blob under
 * `responseType: 'blob'`, so it is read to text, JSON-parsed, and the
 * `ApiResponse` message surfaced (with a friendly 403 string).
 */
export async function downloadSolverInputs(
  scheduleId: string,
): Promise<SolverInputsDownloadResult> {
  try {
    const response = await apiClient.get<Blob>(
      `/schedules/${scheduleId}/export/solver-inputs.xlsx`,
      {
        responseType: "blob",
        headers: { Accept: XLSX_CONTENT_TYPE },
      },
    );
    const disposition = response.headers["content-disposition"];
    const dispositionHeader =
      typeof disposition === "string" ? disposition : null;
    const filename =
      parseContentDispositionFilename(dispositionHeader) ??
      SOLVER_INPUTS_FALLBACK_FILENAME;
    return { blob: response.data, filename };
  } catch (err) {
    if (err instanceof AxiosError) {
      const status = err.response?.status ?? 0;
      // Error responses come back as Blob too (we asked for `responseType:
      // blob`). Read the text and JSON-parse so the backend's
      // `ApiResponse<object>` message surfaces instead of a downloaded file.
      const data = err.response?.data;
      let parsed: unknown = data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          parsed = text.length > 0 ? JSON.parse(text) : null;
        } catch {
          parsed = null;
        }
      }
      throw new Error(extractExportErrorMessage(parsed, status));
    }
    throw err;
  }
}
